/**
 * Composite Event Tracker Adapter
 * Infrastructure layer - Combines multiple event trackers
 * Allows tracking events to multiple analytics providers simultaneously
 */

import { warnOnce } from './logger';
import type { EventTracker } from '../../domain/interfaces/EventTracker';
import type { AnalyticsEventProviderType } from '../../application/services/AnalyticsService';
import { flushAdServerEvent, startAdServerEvent } from '../../utils/adServerEvent';

/**
 * Tracker with Event Provider Type
 * Associates a tracker with its event provider type for filtering
 */
interface TrackerWithEventProvider {
  tracker: EventTracker;
  eventProviderType: AnalyticsEventProviderType;
}

/**
 * Composite Event Tracker
 * Tracks events to multiple providers simultaneously
 * Supports filtering events by service type based on configuration
 */
export class CompositeEventTracker implements EventTracker {
  private trackers: TrackerWithEventProvider[] = [];

  constructor(trackers: TrackerWithEventProvider[] = []) {
    this.trackers = trackers;
  }

  /**
   * Add a tracker to the composite
   * @param tracker - The event tracker to add
   * @param eventProviderType - The type of analytics event provider (ga, webengage, etc.)
   */
  addTracker(tracker: EventTracker, eventProviderType: AnalyticsEventProviderType): void {
    this.trackers.push({ tracker, eventProviderType });
  }

  /**
   * Remove a tracker from the composite
   */
  removeTracker(tracker: EventTracker): void {
    this.trackers = this.trackers.filter((t) => t.tracker !== tracker);
  }

  async initialize(): Promise<void> {
    // Initialize all trackers
    await Promise.all(
      this.trackers.map(({ tracker }) => tracker.initialize?.() || Promise.resolve())
    );
  }

  trackEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options?: { eventProviders?: AnalyticsEventProviderType[] }
  ): void {
    if (this.trackers.length === 0) {
      warnOnce('composite:no-trackers', 'No trackers configured, event not tracked');
      return;
    }

    // Filter trackers based on eventProviders option
    let enabledTrackers = this.trackers;
    
    if (options?.eventProviders && options.eventProviders.length > 0) {
      // Only send to specified event providers
      enabledTrackers = this.trackers.filter(({ eventProviderType }) =>
        options.eventProviders!.includes(eventProviderType)
      );
    }

    if (enabledTrackers.length === 0) {
      warnOnce('composite:no-enabled-providers', 'Event has no enabled trackers for specified event providers');
      return;
    }

    const hasAdPixel = enabledTrackers.some(
      ({ eventProviderType }) => eventProviderType === 'facebook' || eventProviderType === 'openai',
    );
    if (hasAdPixel) {
      startAdServerEvent();
    }

    enabledTrackers.forEach(({ tracker, eventProviderType }) => {
      try {
        tracker.trackEvent(eventName, properties);
      } catch (error) {
        console.error(`Error tracking event "${eventName}" to ${eventProviderType}:`, error);
      }
    });

    if (hasAdPixel) {
      flushAdServerEvent();
    }
  }

  logGoKwikEvent(
    eventName: string,
    attributes: Record<string, unknown> = {},
    eventCategory?: string
  ): void {
    this.trackers.forEach(({ tracker }) => {
      if (typeof tracker.logGoKwikEvent === 'function') {
        try {
          tracker.logGoKwikEvent(eventName, attributes, eventCategory);
        } catch (error) {
          console.error(`Error tracking GoKwik event "${eventName}":`, error);
        }
      }
    });
  }

  logGoKwikSystemEvent(type: string, attributes: Record<string, unknown> = {}): void {
    this.trackers.forEach(({ tracker }) => {
      if (typeof tracker.logGoKwikSystemEvent === 'function') {
        try {
          tracker.logGoKwikSystemEvent(type, attributes);
        } catch (error) {
          console.error(`Error dispatching GoKwik system event "${type}":`, error);
        }
      }
    });
  }

  logEventLogger(payload: Record<string, unknown>): void {
    this.trackers.forEach(({ tracker, eventProviderType }) => {
      if (typeof tracker.logEventLogger !== 'function') {
        return;
      }
      try {
        tracker.logEventLogger(payload);
      } catch (error) {
        console.error(`Error tracking eventLogger payload to ${eventProviderType}:`, error);
      }
    });
  }

  loginUser(
    userCode: string,
    attributes?: { email?: string; firstName?: string; phone?: string }
  ): void {
    this.trackers.forEach(({ tracker }) => {
      if (typeof tracker.loginUser === 'function') {
        try {
          tracker.loginUser(userCode, attributes);
        } catch (error) {
          console.error('Error calling loginUser on tracker:', error);
        }
      }
    });
  }
}

